import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useUser } from '../../context/UserContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import Button from '../UI/Button';
import Select from '../UI/Select';

const PhaseSteps = ({ phaseIndex, control, register, positions }) => {
    const {
        fields: stepFields,
        append: appendStep,
        remove: removeStep,
    } = useFieldArray({
        control,
        name: `route.${phaseIndex}.steps`,
    });

    return (
        <>
            {stepFields.map((step, stepIndex) => (
                <div key={step.id || stepIndex} className="ml-4 mb-2 space-y-2">
                    <input
                        {...register(`route.${phaseIndex}.steps.${stepIndex}.name`)}
                        placeholder="Название шага"
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    />
                    <input
                        type="number"
                        {...register(`route.${phaseIndex}.steps.${stepIndex}.duration`)}
                        placeholder="Длительность (мин)"
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    />
                    <Select
                        {...register(`route.${phaseIndex}.steps.${stepIndex}.roleRequired`)}
                        options={positions.map((pos) => ({
                            value: pos.name,
                            label: pos.name,
                        }))}
                        placeholder="Выберите должность"
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    />
                    <input
                        {...register(`route.${phaseIndex}.steps.${stepIndex}.station`)}
                        placeholder="Оборудование"
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    />
                    <label className="flex items-center gap-2 text-white">
                        <input
                            type="checkbox"
                            {...register(`route.${phaseIndex}.steps.${stepIndex}.canBeSplit`)}
                            className="rounded bg-[#1E1E1E] border-[#3E3E42]"
                        />
                        Можно ли параллелить между рабочими
                    </label>
                    <Button
                        type="button"
                        onClick={() => removeStep(stepIndex)}
                        variant="danger"
                        className="mt-2 bg-[#1E1E1E]"
                    >
                        Удалить шаг
                    </Button>
                </div>
            ))}
            <Button
                type="button"
                onClick={() =>
                    appendStep({
                        name: "",
                        duration: 0,
                        roleRequired: "",
                        station: "",
                        canBeSplit: false,
                    })
                }
                variant="secondary"
                className="mt-2"
            >
                Добавить шаг
            </Button>
        </>
    );
};

const VariationValues = ({ variationIndex, control, register }) => {
    const {
        fields: valueFields,
        append: appendValue,
        remove: removeValue,
    } = useFieldArray({
        control,
        name: `variations.${variationIndex}.value`,
    });

    return (
        <div className="ml-4 space-y-2">
            {valueFields.map((field, valueIndex) => (
                <div key={field.id} className="flex gap-2 items-center">
                    <input
                        {...register(`variations.${variationIndex}.value.${valueIndex}`)}
                        className="flex-1 p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        placeholder="Значение"
                    />
                    <Button
                        type="button"
                        onClick={() => removeValue(valueIndex)}
                        variant="danger"
                        className="px-2 py-1 bg-[#1E1E1E]"
                    >
                        Удалить
                    </Button>
                </div>
            ))}
            <Button
                type="button"
                onClick={() => appendValue("")}
                variant="secondary"
                className="mt-2"
            >
                Добавить значение
            </Button>
        </div>
    );
};

const ProductEditForm = ({ product, onSuccess }) => {
    const { logout } = useUser();
    const [error, setError] = useState(null);
    const [positions, setPositions] = useState([]);
    const [departments, setDepartments] = useState([]);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        defaultValues: product,
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [positionsRes, departmentsRes] = await Promise.all([
                    fetchWithAuth(
                        `${process.env.REACT_APP_API_URL}/products/positions/all`,
                        {},
                        logout
                    ),
                    fetchWithAuth(
                        `${process.env.REACT_APP_API_URL}/products/departments/all`,
                        {},
                        logout
                    )
                ]);
                const positionsData = await positionsRes.json();
                const departmentsData = await departmentsRes.json();
                setPositions(positionsData);
                setDepartments(departmentsData);
            } catch (err) {
                setError("Ошибка загрузки данных");
            }
        };
        fetchData();
    }, [logout]);

    const {
        fields: variationFields,
        append: appendVariation,
        remove: removeVariation,
    } = useFieldArray({
        control,
        name: "variations",
    });

    const {
        fields: phaseFields,
        append: appendPhase,
        remove: removePhase,
    } = useFieldArray({
        control,
        name: "route",
    });

    const onSubmit = async (data) => {
        try {
            await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/products/${product._id}`,
                {
                    method: "PUT",
                    body: JSON.stringify(data),
                },
                logout
            );
            onSuccess();
        } catch (err) {
            setError("Ошибка обновления продукта");
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 text-gray-400 max-w-screen-md mx-auto">
            {error && <div className="text-red-500 mb-4">{error}</div>}

            <div className="space-y-2">
                <input
                    {...register("article", {
                        required: "Укажите артикул",
                    })}
                    className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    placeholder="Артикул"
                />
                {errors.article && (
                    <span className="text-red-500 text-sm">
                        {errors.article.message}
                    </span>
                )}
            </div>

            <div className="space-y-2">
                <input
                    {...register("name", {
                        required: "Укажите название",
                    })}
                    className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    placeholder="Название"
                />
                {errors.name && (
                    <span className="text-red-500 text-sm">
                        {errors.name.message}
                    </span>
                )}
            </div>

            <div className="space-y-2">
                <textarea
                    {...register("description")}
                    className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                    placeholder="Описание"
                />
            </div>

            <div className="border p-4 rounded bg-[#1E1E1E] border-[#3E3E42]">
                <label className="block mb-2 font-semibold text-white">
                    Вариации
                </label>
                {variationFields.map((field, index) => (
                    <div
                        key={field.id}
                        className="mb-4 p-4 border rounded bg-[#2D2D30] border-[#3E3E42]"
                    >
                        <div className="flex gap-4 mb-2">
                            <input
                                {...register(`variations.${index}.key`)}
                                placeholder="Название вариации"
                                className="flex-1 p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                            />
                            <Button
                                type="button"
                                onClick={() => removeVariation(index)}
                                variant="danger"
                                className="bg-[#1E1E1E]"
                            >
                                Удалить вариацию
                            </Button>
                        </div>
                        <VariationValues
                            variationIndex={index}
                            control={control}
                            register={register}
                        />
                    </div>
                ))}
                <Button
                    type="button"
                    onClick={() => appendVariation({ key: "", value: [""] })}
                    variant="secondary"
                    className="mt-2"
                >
                    Добавить вариацию
                </Button>
            </div>

            <div className="border p-4 rounded bg-[#1E1E1E] border-[#3E3E42]">
                <label className="block mb-2 font-semibold text-white">
                    Маршрут производства
                </label>
                {phaseFields.map((field, phaseIndex) => (
                    <div
                        key={field.id}
                        className="mb-4 p-4 border rounded bg-[#2D2D30] border-[#3E3E42]"
                    >
                        <div className="flex gap-4 mb-4">
                            <input
                                {...register(`route.${phaseIndex}.name`)}
                                placeholder="Название фазы"
                                className="flex-1 p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                            />
                            <Select
                                {...register(`route.${phaseIndex}.departmentId`, {
                                    required: "Выберите отдел",
                                })}
                                options={departments.map((dept) => ({
                                    value: dept._id,
                                    label: dept.name,
                                }))}
                                placeholder="Выберите отдел"
                                className="flex-1 p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                            />
                            <Button
                                type="button"
                                onClick={() => removePhase(phaseIndex)}
                                variant="danger"
                                className="bg-[#1E1E1E]"
                            >
                                Удалить фазу
                            </Button>
                        </div>

                        <PhaseSteps
                            phaseIndex={phaseIndex}
                            control={control}
                            register={register}
                            positions={positions}
                        />
                    </div>
                ))}
                <Button
                    type="button"
                    onClick={() =>
                        appendPhase({
                            name: "",
                            departmentId: "",
                            steps: [
                                {
                                    name: "",
                                    duration: 0,
                                    roleRequired: "",
                                    station: "",
                                    canBeSplit: false,
                                },
                            ],
                        })
                    }
                    variant="secondary"
                    className="mt-2"
                >
                    Добавить фазу
                </Button>
            </div>

            <div className="flex justify-end gap-2">
                <Button type="submit" variant="primary">
                    Сохранить
                </Button>
            </div>
        </form>
    );
};

export default ProductEditForm; 